import React, { useCallback, useMemo, useState } from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Heading } from '@fiverr-private/typography';
import { SellerPageFederatedSeller, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { Container, Stack } from '@fiverr-private/layout_components';
import { useCurrentTabSellerRole } from '../RolePageTabContentRoleProvider';
import { usePortfolios } from '../../hooks/usePortfolios';
import { PortfolioSliderSection } from '../PortfolioSliderSection';
import { ServicesAndSkillsTags } from '../ServicesAndSkillsTags';
import { getStartingPriceForAllProjects } from '../PortfolioSliderSection/utils';
import { useBI } from '../BIContext';
import { usePortfolioProjectsByRole, useServicesAndSkills } from './hooks';
import { ITEM_ALL_ID } from './consts';
import { mapProjects } from './utils';

export const ServicesAndSkills = () => {
    const portfolioProjects = usePortfolios();
    const { profileImageUrl, username, displayName } = useSellerData(selectSellerSlice);
    const sellerRole = useCurrentTabSellerRole();
    const { reportServicesTagFilterClicked } = useBI();

    const portfolioProjectsByRole = usePortfolioProjectsByRole(portfolioProjects, sellerRole);
    const servicesAndSkills = useServicesAndSkills(portfolioProjectsByRole, sellerRole);

    const [selectedItemId, setSelectedItemId] = useState(ITEM_ALL_ID);
    const allProjectsPricing = useMemo(
        () => getStartingPriceForAllProjects(portfolioProjectsByRole),
        [portfolioProjectsByRole]
    );

    const selectedItem = useMemo(
        () => servicesAndSkills.find((item) => item.id === selectedItemId),
        [selectedItemId, servicesAndSkills]
    );

    const selectedPortfolioProjects = useMemo(() => {
        const filteredProjects = portfolioProjectsByRole.filter(
            (project) =>
                selectedItemId === ITEM_ALL_ID ||
                project.portfolioProjectRoleCatalog.services.some((service) => service.id === selectedItemId) ||
                project.portfolioProjectRoleCatalog.metadata.some((skill) => skill.id === selectedItemId)
        );

        return mapProjects(filteredProjects, sellerRole?.id);
    }, [selectedItemId, portfolioProjectsByRole, sellerRole]);

    const handleServicesTagClicked = useCallback(
        (itemId?: string | null) => {
            const selectedItemId = itemId || ITEM_ALL_ID;
            setSelectedItemId(selectedItemId);

            reportServicesTagFilterClicked({
                label: selectedItem?.name || '',
                additionalDataMessage: {
                    serviceId: itemId || '',
                    selectedProjectsCount: selectedPortfolioProjects.length,
                },
            });
        },
        [selectedPortfolioProjects, selectedItem, reportServicesTagFilterClicked]
    );

    return (
        <Container>
            <Heading as="h3" marginBottom="6">
                <I18n k="seller_page_perseus.services_tags.title" />
            </Heading>
            <Heading as="h5" marginBottom="6">
                <I18n k="seller_page_perseus.services_tags.subtitle" />
            </Heading>
            <Stack marginBottom="6">
                <ServicesAndSkillsTags items={servicesAndSkills} onChange={handleServicesTagClicked} />
            </Stack>
            <PortfolioSliderSection
                selectedSkillName={selectedItem?.name || ''}
                userName={username}
                avatarSrc={profileImageUrl}
                userDisplayName={displayName}
                allProjectsPricing={allProjectsPricing}
                portfolioProjects={selectedPortfolioProjects}
                selectedServiceId={selectedItemId}
            />
        </Container>
    );
};

const selectSellerSlice = (seller: SellerPageFederatedSeller) => ({
    profileImageUrl: seller.user.profileImageUrl,
    username: seller.user.name,
    displayName: seller.user.profile.displayName,
});
